import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { openSocialOAuth } from "@/app/routes/dashboard/components/settings/socialOAuthPopup";
import { POLL_CURATION_MS } from "@/lib/realtime";

export type SocialProvider = "FACEBOOK" | "INSTAGRAM" | "TIKTOK" | "YOUTUBE" | "SNAPCHAT";
export type SocialAccountType =
  | "FACEBOOK_PAGE"
  | "INSTAGRAM_BUSINESS"
  | "TIKTOK_ACCOUNT"
  | "YOUTUBE_CHANNEL"
  | "SNAPCHAT_PUBLIC_PROFILE";
export type SocialPostType =
  | "FB_POST"
  | "IG_POST"
  | "IG_REEL"
  | "IG_STORY"
  | "TIKTOK_VIDEO"
  | "YT_VIDEO"
  | "SNAP_STORY"
  | "SNAP_SPOTLIGHT"
  | "SNAP_SAVED_STORY";

export type SocialAccount = {
  id: string;
  type: SocialAccountType;
  externalId: string;
  name: string | null;
  username: string | null;
  pictureUrl: string | null;
};

export type SocialConnection = {
  id: string;
  provider: SocialProvider;
  externalUserName: string | null;
  createdAt: string;
  status: "ACTIVE" | "NEEDS_REAUTH";
  authError: string | null;
  /** When it broke (NEEDS_REAUTH). Absent from older API builds. */
  authErrorAt?: string | null;
  pendingPublicationCount: number;
  accounts: SocialAccount[];
};

/** Per-account stats shown under each connection card. Numbers are nullable. */
export type SocialAccountStats = {
  accountId: string;
  provider: SocialProvider;
  accountType: SocialAccountType;
  followerCount: number | null;
  followingCount: number | null;
  postCount: number | null;
  avgEngagement: number | null;
  engagementRate: number | null;
  totalViews: number | null;
  lastSyncedAt: string | null;
};

/** A recent synced post shown under a connection card, keyed to its account. */
export type SocialPost = {
  id: string;
  accountId: string;
  provider: SocialProvider;
  postType: SocialPostType;
  caption: string | null;
  permalink: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  engagement: Record<string, number> | null;
};

const socialConnectionsKey = (brandId: string | null) =>
  ["socialConnections", brandId] as const;

const socialConnectionStatsKey = (brandId: string | null) =>
  ["socialConnectionStats", brandId] as const;

const socialConnectionPostsKey = (brandId: string | null) =>
  ["socialConnectionPosts", brandId] as const;

export function useSocialConnections(brandId: string | null) {
  return useQuery({
    queryKey: socialConnectionsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<SocialConnection[]>(
        `/social/brand/${brandId}/connections`,
      );
      return res.data;
    },
    enabled: !!brandId,
    // The minute-tick sync cron rewrites lastSynced/backoff state server-side.
    refetchInterval: POLL_CURATION_MS,
  });
}

/** Fetches per-account stats, keyed by accountId on the consuming side. */
export function useSocialConnectionStats(brandId: string | null) {
  return useQuery({
    queryKey: socialConnectionStatsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<SocialAccountStats[]>(
        `/social/brand/${brandId}/connections/stats`,
      );
      return res.data;
    },
    enabled: !!brandId,
    refetchInterval: POLL_CURATION_MS,
  });
}

/** Fetches recent synced posts per account, keyed by accountId on the consuming side. */
export function useSocialConnectionPosts(brandId: string | null) {
  return useQuery({
    queryKey: socialConnectionPostsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<SocialPost[]>(
        `/social/brand/${brandId}/connections/posts`,
      );
      return res.data;
    },
    enabled: !!brandId,
    refetchInterval: POLL_CURATION_MS,
  });
}

/**
 * Starts the Facebook-only OAuth flow (Page scopes, no Instagram). On success
 * the browser is redirected to Facebook; it returns to the Settings page when
 * done. Connecting Facebook no longer touches the Instagram connection.
 */
export function useConnectFacebook(brandId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ authUrl: string }>(
        `/social/brand/${brandId}/facebook/connect`,
      );
      return res.data.authUrl;
    },
    onSuccess: (authUrl) => {
      openSocialOAuth(authUrl);
    },
  });
}

/**
 * Starts the Instagram OAuth flow. Instagram is still authorized via Facebook
 * Login (the IG account must be a Business/Creator account linked to a Facebook
 * Page), but it's its own flow now — it only persists an Instagram connection
 * and leaves Facebook untouched. On success the browser is redirected to
 * Facebook; it returns to the Settings page when done.
 */
export function useConnectInstagram(brandId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ authUrl: string }>(
        `/social/brand/${brandId}/instagram/connect`,
      );
      return res.data.authUrl;
    },
    onSuccess: (authUrl) => {
      openSocialOAuth(authUrl);
    },
  });
}

/**
 * Starts the TikTok OAuth flow. A TikTok grant surfaces a single creator
 * account. On success the browser is redirected to TikTok; it returns to the
 * Settings page when done.
 */
export function useConnectTikTok(brandId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ authUrl: string }>(
        `/social/brand/${brandId}/tiktok/connect`,
      );
      return res.data.authUrl;
    },
    onSuccess: (authUrl) => {
      openSocialOAuth(authUrl);
    },
  });
}

/**
 * Starts the YouTube OAuth flow (Google consent). One grant surfaces every
 * channel the Google account owns. Returns to the Settings page when done.
 */
export function useConnectYouTube(brandId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ authUrl: string }>(
        `/social/brand/${brandId}/youtube/connect`,
      );
      return res.data.authUrl;
    },
    onSuccess: (authUrl) => {
      openSocialOAuth(authUrl);
    },
  });
}

/**
 * Starts the Snapchat OAuth flow (Snap Business consent). One grant surfaces
 * every Public Profile the account can reach — its own and any in the
 * organizations it belongs to. Returns to the Settings page when done.
 */
export function useConnectSnapchat(brandId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ authUrl: string }>(
        `/social/brand/${brandId}/snapchat/connect`,
      );
      return res.data.authUrl;
    },
    onSuccess: (authUrl) => {
      openSocialOAuth(authUrl);
    },
  });
}

export function useDisconnectSocialConnection(brandId: string | null) {
  const queryClient = useQueryClient();
  const key = socialConnectionsKey(brandId);

  return useMutation({
    mutationFn: async (connectionId: string) => {
      await axiosInstance.delete(`/social/brand/${brandId}/connections/${connectionId}`);
    },
    // Optimistic: the account leaves the list at once; only it comes back on failure.
    onMutate: async (connectionId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const list = queryClient.getQueryData<SocialConnection[]>(key);
      const index = list?.findIndex((c) => c.id === connectionId) ?? -1;
      const removed = index >= 0 ? list![index] : null;
      queryClient.setQueryData<SocialConnection[]>(key, (old) => (old ?? []).filter((c) => c.id !== connectionId));
      return { removed, index };
    },
    onError: (_err, _connectionId, context) => {
      if (!context?.removed) return;
      const { removed, index } = context;
      queryClient.setQueryData<SocialConnection[]>(key, (old) => {
        const next = [...(old ?? [])];
        if (next.some((c) => c.id === removed.id)) return next;
        next.splice(Math.max(0, index), 0, removed);
        return next;
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
